import { useCodeListQuery } from "Api/Queries/CodeList/useCodeListQuery";
import type { CurrencyCode } from "Infrastructure/Api/Api";
import type { CodeListItem } from "Models/ICodeListDto";
import { Resources, useResource } from "Translations/Resources";
import { getCurrencySymbol } from "Utils/CurrencyUtils";
import { useMemo } from "react";

/**
 * Mirror of useRegionList over currencies: the codelist is the single source of truth for which
 * currencies exist (display universe) and which are open on this environment. Disabled items render
 * greyed out with a "coming soon" tooltip instead of disappearing.
 */
export const useCurrencyList = () => {
  const { t } = useResource();

  const { data, isLoading, isError } = useCodeListQuery();
  const currencies = data?.currencies;
  // Older API without the field ⇒ everything enabled — the BE is the authority on what is open.
  const enabledCurrencies = useMemo<Array<CurrencyCode>>(
    () => data?.enabledCurrencies ?? currencies ?? [],
    [data?.enabledCurrencies, currencies],
  );

  const codeListCurrencies = useMemo<Array<CodeListItem<CurrencyCode>>>(
    () =>
      currencies?.map((code) => {
        const isEnabled = enabledCurrencies.includes(code);
        const name = getCurrencySymbol(code);
        return {
          code,
          // The "coming soon" note lives in the label itself — always visible, works in the
          // mobile shells (no hover), and needs no change to the shared Dropdown.
          name: isEnabled
            ? name
            : `${name} (${t(Resources.Components.CountrySelector.ComingSoon)})`,
          disabled: !isEnabled,
        };
      }) ?? [],
    [currencies, enabledCurrencies, t],
  );

  return {
    isLoading,
    isError,
    currencies: currencies ?? [],
    enabledCurrencies,
    codeListCurrencies,
  };
};
